<template>
  <div>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            v-if="$route.name != `ProfilePage`"
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          <span>
            {{ $route.name == "ProfilePage" ? `My Profile` : `Edit User` }}
          </span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="4">
              <v-text-field
                v-model="form.first_name"
                outlined
                label="First Name *"
                prepend-inner-icon="mdi-account"
                :error-messages="errors.first_name"
                @keypress="onlyAlphabets"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.middle_name"
                outlined
                label="Middle Name"
                prepend-inner-icon="mdi-account"
                :error-messages="errors.middle_name"
                @keypress="onlyAlphabets"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.last_name"
                outlined
                label="Last Name"
                prepend-inner-icon="mdi-account"
                :error-messages="errors.last_name"
                @keypress="onlyAlphabets"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.user_name"
                outlined
                label="Username"
                prepend-inner-icon="mdi-at"
                :error-messages="errors.user_name"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.phone"
                outlined
                label="Phone *"
                prepend-inner-icon="mdi-phone"
                :error-messages="errors.phone"
                @keypress="onlyNumbers"
                maxlength="10"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.email"
                outlined
                label="Email"
                prepend-inner-icon="mdi-email"
                :error-messages="errors.email"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-select
                v-model="form.gender"
                outlined
                label="Gender"
                prepend-inner-icon="mdi-gender-male-female"
                :items="genderOptions"
                :error-messages="errors.gender"
              ></v-select>
            </v-col>
            <v-col sm="4" v-if="$route.name != 'ProfilePage'">
              <v-select
                v-model="form.role_id"
                outlined
                label="Role"
                prepend-inner-icon="mdi-shield-account"
                :items="roleOptions"
                item-text="name"
                item-value="id"
                :error-messages="errors.role_id"
              ></v-select>
            </v-col>
            <v-col sm="4">
              <v-menu
                v-model="dobMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.dob"
                    outlined
                    label="Date of Birth"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :error-messages="errors.dob"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.dob"
                  :max="new Date().toISOString().substr(0, 10)"
                  @input="dobMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.pincode"
                outlined
                label="Pincode"
                prepend-inner-icon="mdi-map-marker"
                :error-messages="errors.pincode"
                @keypress="onlyNumbers"
                maxlength="6"
              ></v-text-field>
            </v-col>
            <v-col sm="12">
              <v-text-field
                v-model="form.address"
                outlined
                label="Address"
                prepend-inner-icon="mdi-home"
                :error-messages="errors.address"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                outlined
                v-model="form.password"
                :append-icon="show_password ? 'mdi-eye' : 'mdi-eye-off'"
                :type="show_password ? 'text' : 'password'"
                :error-messages="errors.password"
                label="New Password"
                placeholder="Leave blank to keep current password"
                @click:append="show_password = !show_password"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                outlined
                v-model="form.password_confirmation"
                :append-icon="show_password_confirmation ? 'mdi-eye' : 'mdi-eye-off'"
                :type="show_password_confirmation ? 'text' : 'password'"
                :error-messages="errors.password_confirmation"
                label="Confirm New Password"
                placeholder="Confirm new password"
                @click:append="show_password_confirmation = !show_password_confirmation"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.min_deposit_amount"
                outlined
                label="Min Deposit Amount"
                prepend-inner-icon="mdi-cash-plus"
                @keypress="onlyNumbers"
                :error-messages="errors.min_deposit_amount"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.min_withdrawal_amount"
                outlined
                label="Min Withdrawal Amount"
                prepend-inner-icon="mdi-cash-minus"
                @keypress="onlyNumbers"
                :error-messages="errors.min_withdrawal_amount"
              ></v-text-field>
            </v-col>
            <v-col sm="6" v-if="$route.name != 'ProfilePage'">
              <v-switch
                v-model="form.is_active"
                :true-value="1"
                :false-value="0"
                label="Active"
                color="primary"
                inset
                hide-details
                class="mt-0 pt-0"
              ></v-switch>
            </v-col>
          </v-row>
          <v-divider class="mt-5 mb-5"> </v-divider>
          <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            color="primary"
            rounded
            dark
            @click="save"
          >
            Update {{ $route.name == "ProfilePage" ? `Profile` : `User` }}
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>
<script>
import axios from "axios";
export default {
  name: "UserEditPage",
  data() {
    return {
      form: {
        user_devices: [],
        user_locations: [],
        first_name: '',
        middle_name: '',
        last_name: '',
        user_name: '',
        email: '',
        phone: '',
        gender: null,
        dob: null,
        address: '',
        pincode: '',
        password: '',
        password_confirmation: '',
        min_deposit_amount: '',
        min_withdrawal_amount: '',
        is_active: 1,
      },
      genderOptions: ['Male', 'Female', 'Other'],
      roleOptions: [],
      dobMenu: false,
      errors: {},
      isLoading: false,
      show_password: false,
      show_password_confirmation: false,
      old_json: {},
    };
  },
  mounted() {
    this.getData();
    this.getRoles();
  },
  methods: {
    async getRoles() {
      try {
        const response = await axios.get('/roles');
        this.roleOptions = response.data || [];
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    },
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.userId;
      try {
        const response = await axios.get(`/users/${encryptedId}`);
        this.form = { ...this.form, ...response.data.data };
        // Set current role
        if (this.form.roles && this.form.roles.length > 0) {
          this.form.role_id = this.form.roles[0].id;
        }
        // Clear password fields for security
        this.form.password = '';
        this.form.password_confirmation = '';
        this.old_json = response.data.data;
      } catch (error) {
        console.error("Error fetching data:", error);
        this.$store.dispatch('showSnackbar', {
          message: 'Error loading user data',
          color: 'error'
        });
      }
      this.isLoading = false;
    },

    async save() {
      try {
        if (!this.validateForm()) {
          return;
        }

        this.isLoading = true;
        this.clearErrors();

        const formData = { ...this.form };
        if (!formData.password) {
          delete formData.password;
          delete formData.password_confirmation;
        }

        await axios.patch(`/users/${this.$route.params.userId}`, formData);

        this.isLoading = false;

        this.$store.dispatch('showSnackbar', {
          message: 'User updated successfully!',
          color: 'success'
        });

        await this.saveUserTimestamp(this.old_json, this.form);

        if (this.$route.name == "ProfilePage") {
          // Stay on profile page
        } else {
          this.$router.push("/users");
        }

      } catch (e) {
        this.isLoading = false;

        if (e.response && e.response.data && e.response.data.errors) {
          this.errors = e.response.data.errors;
        } else {
          this.$store.dispatch('showSnackbar', {
            message: 'Error updating user. Please try again.',
            color: 'error'
          });
        }
      }
    },

    validateForm() {
      const errors = {};

      if (!this.form.first_name) errors.first_name = ['First name is required'];
      if (!this.form.phone) errors.phone = ['Phone is required'];

      if (this.form.password && this.form.password_confirmation &&
          this.form.password !== this.form.password_confirmation) {
        errors.password_confirmation = ['Password confirmation does not match'];
      }

      if (this.form.password && !this.form.password_confirmation) {
        errors.password_confirmation = ['Password confirmation is required when changing password'];
      }

      if (this.form.email && !this.isValidEmail(this.form.email)) {
        errors.email = ['Please enter a valid email address'];
      }

      if (this.form.phone && this.form.phone.length < 10) {
        errors.phone = ['Phone number must be 10 digits'];
      }

      if (this.form.pincode && this.form.pincode.length < 6) {
        errors.pincode = ['Pincode must be 6 digits'];
      }

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    clearErrors() {
      this.errors = {};
    },
  },
};
</script>
